/**
 * DefaultOnboardingContentEditorV2 — V2-native content editor panel
 *
 * Renders editor sections based on V2 screen type config.
 * All 17 sections are now native V2, working directly with StepNode data.
 *
 * Key differences from DefaultOnboardingContentEditor (V1):
 * - Input: FlowStepNode (not Screen)
 * - Single onChange callback: onContentChange (V2 path-based)
 * - Uses getV2ScreenTypeConfig instead of getOnboardingScreenTypeConfig
 * - All sections: headerBar, actionBar, backdrop, copyBlock, basicConfig (Sprint 3)
 *   + options, carousel, stepper, textField, roller, media, embed (Sprint 4)
 *   + paywallHeader, paywallMedia, paywallBody, paywallSubscriptions, paywallFooter (Sprint 4)
 *
 * Sprint: schema-v2-migration, Sprint 3 + Sprint 4
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { GuideAnchorProvider } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { EditorGuideProvider } from '@modules/reactFlow/contexts';
import { LAYOUT_CONTENT_PADDING } from '../../config/ui';
import { getV2ScreenTypeConfig, type V2SectionType } from '../../config/v2ScreenTypeConfig';
import { SCREEN_EDITOR_SECTION_GUIDE_ANCHORS_BY_TEST_ID } from '@modules/reactFlow/config/v2ScreenEditorGuideAnchors';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument } from '@segmently/schema-models/v2';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';

// Sprint 3 native sections
import {
  V2HeaderBarSection,
  V2ActionBarSection,
  V2BackdropSection,
  V2CopyBlockSection,
  V2BasicConfigSection,
  V2AnimationSection,
} from './editorSectionsV2';

// Sprint 4 native sections
import { V2TextFieldSection } from './editorSectionsV2/V2TextFieldSection';
import { V2MediaSection } from './editorSectionsV2/V2MediaSection';
import { V2EmbedSection } from './editorSectionsV2/V2EmbedSection';
import { V2RollerSection } from './editorSectionsV2/V2RollerSection';
import { V2OptionsListSection } from './editorSectionsV2/V2OptionsListSection';
import { V2CarouselSection } from './editorSectionsV2/V2CarouselSection';
import { V2StepperSection } from './editorSectionsV2/V2StepperSection';

// Sprint 4 — Paywall sections
import { V2PaywallHeaderSection } from './editorSectionsV2/V2PaywallHeaderSection';
import { V2PaywallMediaSection } from './editorSectionsV2/V2PaywallMediaSection';
import { V2PaywallBodySection } from './editorSectionsV2/V2PaywallBodySection';
import { V2PaywallSubscriptionsSection } from './editorSectionsV2/V2PaywallSubscriptionsSection';
import { V2PaywallFooterSection } from './editorSectionsV2/V2PaywallFooterSection';
import { V2FlexibleSectionsSection } from './editorSectionsV2/V2FlexibleSectionsSection';
import { V2StickyContainerSection } from './editorSectionsV2/V2StickyContainerSection';
import { V2VariableBindingSection } from './editorSectionsV2/V2VariableBindingSection';
import { AiMetaSection } from './editorSectionsV2/AiMetaSection';
import { AiAssetsSection } from './editorSectionsV2/AiAssetsSection';
import type {
  GenerateImageConfig,
  GenerateImageResult,
  RegenerateAssetPromptConfig,
  RegenerateAssetPromptResult,
} from './editorSectionsV2/AiAssetsSection';
import type { MeasureAiAssetSlotContext } from '@modules/reactFlow/utils/aiAssetScreenContext';
import { getActiveV2EditorSections } from '@modules/reactFlow/utils/v2EditorSections';

export interface DefaultOnboardingContentEditorV2Props {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Callback for V2 native section changes (path-based) */
  onContentChange: (path: string, value: unknown) => void;

  /** Whether editor is saving */
  isSaving?: boolean;

  /** Current editing locale */
  locale: string;

  /** Available locales */
  availableLocales?: string[];

  /** Default language from version */
  defaultLanguage: string;

  /** Available languages from version */
  languages: string[];

  /** Project ID for upload service */
  projectId?: string;

  /** Onboarding ID for upload service */
  onboardingId?: string;

  /** Version ID for upload service */
  versionId?: string;

  /** Animation control callbacks (for carousel/stepper sections) */
  onPreviewPlay?: () => void;
  onPreviewStop?: () => void;
  isPreviewPlaying?: boolean;

  /**
   * Fired by V2AnimationSection after any animation setting changes —
   * the editor dialog auto-replays the preview (rising edge of
   * isAnimationPlaying) so the user immediately sees the new motion.
   */
  onAnimationSettingsChange?: () => void;

  /** Full FlowDocument for variable resolution (BFS-reachable variables at this screen) */
  flowDocument?: FlowDocument | null;

  /** Theme settings snapshot for themed defaults on element toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;

  /** Editor mode: 'full' (default) shows all sections, 'ai' shows AI-specific sections */
  mode?: 'full' | 'ai';

  /** Callback for AI image generation in AiAssetsSection */
  onGenerateImage?: (slotKey: string, config: GenerateImageConfig) => Promise<GenerateImageResult>;

  /** Callback for backend prompt regeneration in AiAssetsSection */
  onRegenerateAssetPrompt?: (slotKey: string, config: RegenerateAssetPromptConfig) => Promise<RegenerateAssetPromptResult>;

  /** Measures real rendered V2ScreenView context for an AI asset slot. */
  measureAiAssetSlotContext?: MeasureAiAssetSlotContext;

  /** Applies a completed AI asset experiment URL into the V2 content image path. */
  onApplyGeneratedAsset?: (slotKey: string, experimentId: string) => void;

  /** Applies all completed selected AI asset URLs into their V2 content image paths. */
  onApplyAllGeneratedAssets?: () => void;

  /** Strategy ID — for AI asset upload path. NOT the same as onboardingId. */
  strategyId?: string;

  /** Strategy block ID — for AI asset upload path. NOT the same as versionId. */
  strategyBlockId?: string;

  /**
   * M7: optional save handler exposed by the screen editor dialog.
   * Forwarded to V2PaywallSubscriptionsSection so the paywall product
   * picker can offer "Save and connect Stripe" before redirecting.
   */
  onSaveScreen?: () => Promise<void>;

  /**
   * M7: whether the editor dialog has unsaved changes. Drives the
   * picker's save-guard modal button set.
   */
  hasUnsavedChanges?: boolean;
}

export const DefaultOnboardingContentEditorV2: React.FC<DefaultOnboardingContentEditorV2Props> = ({
  step,
  onContentChange,
  isSaving = false,
  locale,
  availableLocales = [],
  defaultLanguage: _defaultLanguage,
  languages: _languages,
  projectId,
  onboardingId,
  versionId,
  onPreviewPlay,
  onPreviewStop,
  isPreviewPlaying,
  onAnimationSettingsChange,
  flowDocument,
  themeSettingsV2,
  mode = 'full',
  onGenerateImage,
  onRegenerateAssetPrompt,
  measureAiAssetSlotContext,
  onApplyGeneratedAsset,
  onApplyAllGeneratedAssets,
  strategyId,
  strategyBlockId,
  onSaveScreen,
  hasUnsavedChanges,
}) => {
  const theme = useTheme();
  const guideService = useMemo(() => new GuideService(), []);

  // Get V2 section config for this screen type
  const screenType = step.screenType || '';
  const config = getV2ScreenTypeConfig(screenType);

  // Select sections based on editor mode, while keeping screen-owned asset slots
  // editable from the screen editor even when the dialog opens in full mode.
  const activeSections = getActiveV2EditorSections(config, mode, step);

  // ── Subsection badge state ──
  // Tracks badge counts from embedded subsections (e.g. variableBinding inside options).
  // Key = subsection type, value = badge count reported by that subsection.
  const [subsectionBadges, setSubsectionBadges] = useState<Partial<Record<V2SectionType, number>>>({});

  const handleSubsectionBadge = useCallback((type: V2SectionType, count: number) => {
    setSubsectionBadges(prev => prev[type] === count ? prev : { ...prev, [type]: count });
  }, []);

  // Set of section types that are subsections (should NOT render as top-level sections)
  const subsectionSet = config.subsections ?? {};

  // Helper: build renderSubsections closure + badge props for a parent section
  const getSubsectionProps = (parentType: V2SectionType) => {
    // Find all subsections whose parent is this section type
    const children = Object.entries(subsectionSet).filter(
      ([, cfg]) => cfg?.parent === parentType
    ) as [V2SectionType, NonNullable<typeof subsectionSet[V2SectionType]>][];

    if (children.length === 0) return {};

    // Aggregate badge counts from all child subsections
    const totalBadge = children.reduce((sum, [childType]) => sum + (subsectionBadges[childType] || 0), 0);
    // Use position from first child (all children of same parent should share position)
    const position = children[0][1].position;

    const renderSubsections = () => (
      <>
        {children.map(([childType, childConfig]) => {
          // Currently only variableBinding is supported as subsection
          if (childType === 'variableBinding') {
            return (
              <V2VariableBindingSection
                key="variableBinding-embedded"
                step={step}
                locale={locale}
                onChange={onContentChange}
                disabled={isSaving}
                defaultExpanded={childConfig.defaultExpanded}
                mode="embedded"
                onBadgeCount={(count) => handleSubsectionBadge('variableBinding', count)}
              />
            );
          }
          return null;
        })}
      </>
    );

    return {
      renderSubsections,
      subsectionBadgeCount: totalBadge,
      subsectionPosition: position,
    };
  };

  const renderSection = (sectionType: V2SectionType) => {
    // Skip sections that are configured as subsections (they render inside their parent)
    if (subsectionSet[sectionType]) return null;
    switch (sectionType) {
      // ── Sprint 3 native sections ──
      case 'headerBar':
        return (
          <V2HeaderBarSection
            key="headerBar"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            themeSettingsV2={themeSettingsV2}
          />
        );

      case 'actionBar':
        return (
          <V2ActionBarSection
            key="actionBar"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            themeSettingsV2={themeSettingsV2}
          />
        );

      case 'backdrop':
        return (
          <V2BackdropSection
            key="backdrop"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
          />
        );

      case 'copyBlock':
        return (
          <V2CopyBlockSection
            key="copyBlock"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            flowDocument={flowDocument}
            themeSettingsV2={themeSettingsV2}
          />
        );

      case 'basicConfig':
        return (
          <V2BasicConfigSection
            key="basicConfig"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
          />
        );

      case 'animation':
        return (
          <V2AnimationSection
            key="animation"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
            flowDocument={flowDocument}
            onAnimationSettingsChange={onAnimationSettingsChange}
          />
        );

      // ── Sprint 4 native sections ──
      case 'textField':
        return (
          <V2TextFieldSection
            key="textField"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            themeSettingsV2={themeSettingsV2}
            {...getSubsectionProps('textField')}
          />
        );

      case 'media':
        return (
          <V2MediaSection
            key="media"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            themeSettingsV2={themeSettingsV2}
          />
        );

      case 'embed':
        return (
          <V2EmbedSection
            key="embed"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
          />
        );

      case 'roller':
        return (
          <V2RollerSection
            key="roller"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            {...getSubsectionProps('roller')}
          />
        );

      case 'options':
        return (
          <V2OptionsListSection
            key="options"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            themeSettingsV2={themeSettingsV2}
            {...getSubsectionProps('options')}
          />
        );

      case 'carousel':
        return (
          <V2CarouselSection
            key="carousel"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            onPreviewPlay={onPreviewPlay}
            onPreviewStop={onPreviewStop}
            isPreviewPlaying={isPreviewPlaying}
            flowDocument={flowDocument}
          />
        );

      case 'stepper':
        return (
          <V2StepperSection
            key="stepper"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            onPreviewPlay={onPreviewPlay}
            onPreviewStop={onPreviewStop}
            isPreviewPlaying={isPreviewPlaying}
            flowDocument={flowDocument}
          />
        );

      // ── Sprint 4 — Paywall sections ──
      case 'paywallHeader':
        return (
          <V2PaywallHeaderSection
            key="paywallHeader"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
          />
        );

      case 'paywallMedia':
        return (
          <V2PaywallMediaSection
            key="paywallMedia"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
          />
        );

      case 'paywallBody':
        return (
          <V2PaywallBodySection
            key="paywallBody"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            flowDocument={flowDocument}
          />
        );

      case 'paywallSubscriptions':
        return (
          <V2PaywallSubscriptionsSection
            key="paywallSubscriptions"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onSaveScreen={onSaveScreen}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        );

      case 'paywallFooter':
        return (
          <V2PaywallFooterSection
            key="paywallFooter"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            flowDocument={flowDocument}
          />
        );

      case 'flexibleSections':
        return (
          <V2FlexibleSectionsSection
            key="flexibleSections"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={true}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            flowDocument={flowDocument}
            themeSettingsV2={themeSettingsV2}
          />
        );

      case 'stickyContainer':
        return (
          <V2StickyContainerSection
            key="stickyContainer"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            themeSettingsV2={themeSettingsV2}
          />
        );

      // ── Variable system ──
      case 'variableBinding':
        return (
          <V2VariableBindingSection
            key="variableBinding"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            defaultExpanded={false}
          />
        );

      // ── AI mode sections ──
      case 'aiMeta':
        return (
          <AiMetaSection
            key="aiMeta"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            projectId={projectId}
          />
        );

      case 'aiAssets':
        return (
          <AiAssetsSection
            key="aiAssets"
            step={step}
            locale={locale}
            onChange={onContentChange}
            disabled={isSaving}
            onGenerateImage={onGenerateImage}
            onRegeneratePrompt={onRegenerateAssetPrompt}
            measureSlotContext={measureAiAssetSlotContext}
            onApplyGeneratedAsset={onApplyGeneratedAsset}
            onApplyAllGeneratedAssets={onApplyAllGeneratedAssets}
            projectId={projectId}
            strategyId={strategyId}
            blockId={strategyBlockId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <GuideAnchorProvider
      guideService={guideService}
      anchorsByTestId={SCREEN_EDITOR_SECTION_GUIDE_ANCHORS_BY_TEST_ID}
    >
      <EditorGuideProvider guideService={guideService}>
        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            padding: theme.spacing(LAYOUT_CONTENT_PADDING),
            backgroundColor: 'transparent',
          }}
        >
          {activeSections.map((sectionType) => renderSection(sectionType))}
        </Box>
      </EditorGuideProvider>
    </GuideAnchorProvider>
  );
};
